import {
  faComments,
  faEdit,
  faLinkSlash,
  faMinus,
  faPlus,
  faShareNodes,
  faSpinner,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CloseButton } from "../components/CloseButton";
import { SideBar } from "../components/SideBar";
import { useAccount, useProjects, useToast } from "../state";
import { WebDavLink } from "./WebDavLink";

export const NewProject = () => {
  const { createProject } = useProjects();
  const [open, setOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newProjectName.trim()) {
      await createProject(newProjectName);
      setNewProjectName("");
    }
  };
  const btn = (
    <button
      onClick={() => setOpen(!open)}
      className={
        "px-1 py-1 float-right mr-4 rounded text-white " +
        (open ? "bg-red-500 hover:bg-red-700" : "bg-green hover:bg-green-800")
      }
      style={{ marginTop: "-50px" }}
    >
      <FontAwesomeIcon icon={open ? faMinus : faPlus} />
    </button>
  );

  if (!open) return btn;
  return (
    <>
      {btn}
      <form onSubmit={handleCreate} className="mb-6 flex gap-2">
        <input
          type="text"
          value={newProjectName}
          onChange={(e) => setNewProjectName(e.target.value)}
          placeholder="Nieuw project naam"
          className="flex-1 p-2 border rounded text-black"
        />
        <button
          type="submit"
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Toevoegen
        </button>
      </form>
    </>
  );
};

// Deel-link beheren: aanmaken kopieert de publieke /shared/<token>-URL,
// intrekken maakt de link ongeldig (opnieuw delen geeft een nieuwe token).
const ShareButton = ({ project }) => {
  const fetchProjects = useProjects((s) => s.fetchProjects);
  const show = useToast((s) => s.show);

  const copy = async (token: string) => {
    const url = `${window.location.origin}/shared/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      show("Deel-link gekopieerd naar het klembord");
    } catch {
      // Safari kan kopiëren na een fetch weigeren (user-gesture verlopen):
      // toon de link dan selecteerbaar in de melding
      show(`Kopieer de deel-link: ${url}`, 15000);
    }
  };
  const share = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const r = await fetch(`/api/projects/${project.id}/share`, { method: "POST" });
    const { token } = await r.json();
    await copy(token);
    fetchProjects(true);
  };
  const unshare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await fetch(`/api/projects/${project.id}/share`, { method: "DELETE" });
    show("Delen gestopt — de link is niet meer geldig");
    fetchProjects(true);
  };

  return (
    <>
      <button
        onClick={share}
        className={
          project.shareToken ? "text-green hover:text-dark-green" : "text-gray-400 hover:text-green"
        }
        title={project.shareToken ? "Gedeeld — kopieer link" : "Deel via publieke link"}
      >
        <FontAwesomeIcon icon={faShareNodes} />
      </button>
      {project.shareToken && (
        <button
          onClick={unshare}
          className="text-gray-400 hover:text-red-600"
          title="Stop met delen (link vervalt)"
        >
          <FontAwesomeIcon icon={faLinkSlash} />
        </button>
      )}
    </>
  );
};

const Project = ({ project, edit }) => {
  const { deleteProject, selectProject } = useProjects();
  const workspace = useAccount((s) => s.user?.features?.workspace);
  const navigate = useNavigate();

  const handleSelect = useCallback(
    (e: React.MouseEvent) => {
      // Prevent selection when clicking edit or delete buttons
      if ((e.target as HTMLElement).closest("button")) return;

      selectProject(project.id);
      navigate(`/projects/${project.id}`);
    },
    [project.id, selectProject, navigate]
  );

  return (
    <div onClick={handleSelect} className="cursor-pointer">
      <div className="flex justify-between items-start">
        <h2 className="text-black">
          <button
            onClick={() => selectProject(project.id)}
            className="text-black hover:text-blue-500"
          >
            {project.name}
          </button>
        </h2>
        <div className="flex gap-2">
          {workspace && (
            <button
              onClick={() => navigate(`/workspace/${project.id}`)}
              className="text-gray-400 hover:text-black"
              title="Open workspace"
              aria-label="Open workspace"
            >
              <FontAwesomeIcon icon={faComments} />
            </button>
          )}
          <ShareButton project={project} />
          <button
            onClick={(e) => {
              e.stopPropagation();
              edit(project);
            }}
            className="text-blue-500 hover:text-blue-700"
            title="Bewerken"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirm("Weet je het zeker?")) deleteProject(project.id);
            }}
            className="text-gray-500 hover:text-red-500"
            title="Verwijderen"
          >
            <FontAwesomeIcon icon={faTrashCan} />
          </button>
        </div>
      </div>
      <p className="text-gray-600">
        <a href={project.link || ""} onClick={(e) => e.stopPropagation()}>
          {project.description}
        </a>
      </p>
    </div>
  );
};

export const Projects = () => {
  const { projects, fetchProjects, updateProject, selectProject, state } = useProjects();
  const [editingProjectId, setEditingProjectId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");
  const { id } = useParams();
  useEffect(() => selectProject((id && parseInt(id)) || null), [id, selectProject]);

  if (!projects) {
    return (
      <SideBar>
        <div className="p-6">
          <div>
            <FontAwesomeIcon icon={faSpinner} spin={true} fill="green" />
            {state === "error" ? (
              <>
                Fout...{" "}
                <button
                  className="text-white bg-green hover:bg-green-800 rounded px-2 py-1"
                  onClick={() => fetchProjects(true)}
                >
                  opnieuw
                </button>
              </>
            ) : (
              "Laden..."
            )}
          </div>
        </div>
      </SideBar>
    );
  }

  const selectedProjectId = id ? parseInt(id) : null;

  const handleUpdate = async (e: React.FormEvent, id: number) => {
    e.preventDefault();
    if (editingName.trim()) {
      await updateProject(id, editingName);
      setEditingProjectId(null);
    }
  };

  const startEditing = (project: any) => {
    setEditingProjectId(project.id);
    setEditingName(project.name);
  };

  return (
    <SideBar>
      <div className="p-4 pb-14">
        <CloseButton className="text-black hover:text-gray hover:bg-black" />
        <h1 className="text-xl font-semibold mb-3">Locaties en projecten </h1>
        <WebDavLink />
        <NewProject />
        <ul>
          {projects.map((project) => (
            <li
              key={project.id}
              className={`p-2 border-gray-100 border-b first:rounded-t last:rounded-b shadow ${
                selectedProjectId === project.id ? "bg-sidebar-menu-icon" : "bg-white"
              }`}
            >
              {editingProjectId === project.id ? (
                <form onSubmit={(e) => handleUpdate(e, project.id)} className="flex flex-col gap-2">
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="p-2 border rounded text-black"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="bg-green-500 text-white px-3 py-1 rounded text-sm"
                    >
                      Opslaan
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingProjectId(null)}
                      className="bg-gray-500 text-white px-3 py-1 rounded text-sm"
                    >
                      Annuleren
                    </button>
                  </div>
                </form>
              ) : (
                <Project project={project} edit={startEditing} />
              )}
            </li>
          ))}
        </ul>
      </div>
    </SideBar>
  );
};
